import { useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { config } from './config';
import { createPortalTheme } from './theme';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import { OidcProvider } from './contexts/OidcContext';
import { useOidc } from './hooks/useOidc';
import { LocaleContext, getLocale } from './i18n';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/Layout';
import ServersPage from './pages/ServersPage';
import ServerDetailPage from './pages/ServerDetailPage';
import MyRequestsPage from './pages/MyRequestsPage';
import PricingPage from './pages/PricingPage';
import SeatsPage from './pages/SeatsPage';
import UsagePage from './pages/UsagePage';
import LoginPage from './pages/LoginPage';
import HelpPage from './pages/HelpPage';
import WorkflowsPage from './pages/WorkflowsPage';
import WorkflowDetailPage from './pages/WorkflowDetailPage';
import AuthGuard from './components/AuthGuard';
import VisualizationPage from './pages/VisualizationPage';

function isValidOrganization(org: string): boolean {
  return /^[a-zA-Z][a-zA-Z0-9_]{2,59}$/.test(org);
}

function getOrganization(): string {
  const params = new URLSearchParams(window.location.search);
  const org = params.get('organization') || '';
  if (org) {
    if (!isValidOrganization(org)) {
      console.warn('Invalid organization parameter ignored');
      return localStorage.getItem('portal_organization') || config.oidc.organization;
    }
    localStorage.setItem('portal_organization', org);
    return org;
  }
  return localStorage.getItem('portal_organization') || config.oidc.organization;
}

function ThemedApp() {
  const branding = useBranding();
  const theme = useMemo(
    () => createPortalTheme(branding),
    [branding],
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<AuthGuard />}>
            <Route path="/visualizations/:workflowName/:nodeId" element={<VisualizationPage />} />
            <Route element={<Layout />}>
              <Route path="/" element={<ServersPage />} />
              <Route path="/servers/:name" element={<ServerDetailPage />} />
              <Route path="/workflows" element={<WorkflowsPage />} />
              <Route path="/workflows/:name" element={<WorkflowDetailPage />} />
              <Route path="/my-requests" element={<MyRequestsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/seats" element={<SeatsPage />} />
              <Route path="/usage" element={<UsagePage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

function OidcApp() {
  const oidc = useOidc();

  return (
    <OidcProvider value={oidc}>
      <BrandingProvider>
        <ThemedApp />
      </BrandingProvider>
    </OidcProvider>
  );
}

function App() {
  // useState lazy-init fires exactly once; calling getOrganization() in render would re-read storage every render.
  useState(getOrganization);
  const [locale] = useState(getLocale);

  return (
    <ErrorBoundary>
      <LocaleContext.Provider value={locale}>
        <OidcApp />
      </LocaleContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
